import React, { useState } from 'react';
import PropTypes from 'prop-types';

import { GLSLCanvas } from '../../base/zoo';

import './Sundial.scss';

// See Clock.jsx
function timeDigits(unixTime, timeZone) {

  const localeTimeString = new Date(unixTime).toLocaleTimeString('en-US', { timeZone });
  const matches = localeTimeString.match(/(\d+):(\d+):(\d+)\s([AP])M/);

  if (matches) {
    let hh = matches[1];
    const mm = matches[2];
    const ss = matches[3];
    if (hh !== '12' && matches[4] === 'P') {
      hh = `${parseInt(hh, 10) + 12}`;
    }
    if (hh === '12' && matches[4] !== 'P') {
      hh = '0';
    }
    if (hh.length === 1) {
      hh = '0' + hh;
    }
    return `${hh}:${mm}:${ss}`;
  }
  return '';
}

function dateDigits(unixTime, timeZone) {
  const localeDateString = new Date(unixTime).toLocaleDateString('en-US', { timeZone });
  const matches = localeDateString.match(/(\d+)\/(\d+)\/(\d\d\d\d)/);
  if (matches) {
    const mm = parseInt(matches[1], 10);
    const month = ([null, 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'])[mm];
    const dd = matches[2];
    const yyyy = matches[3];
    return `${yyyy}${month}${dd}`;
  }
  return '';
}

const Sundial = (props) => {

  const { refreshInterval } = props;

  const [unixTime, setUnixTime] = useState(Date.now());

  const seconds = Math.floor(unixTime / 1000);

  const minuteCount = new Date(unixTime).getMinutes();

  setTimeout(() => { setUnixTime(Date.now()); }, refreshInterval);

  const WIDTH = 1920;
  const HEIGHT = 1080;

  const BOX_WIDTH = 550;
  const BOX_HEIGHT = 120;

  const BOTTOM_BOX_WIDTH = 320;
  const BOTTOM_BOX_HEIGHT = 30;
  const NUDGE = 5;

  const RADIUS = 360;
  const NUMERAL_RADIUS = 320;

  // const denverTime = parseTime(unixTime, 'America/Denver');
  // const easternTime = parseTime(unixTime, 'America/New_York');
  // const utcTime = parseTime(unixTime, 'UTC');
  //
  return (
    <div className="dashboard Sundial">
      <GLSLCanvas
        className="fullscreen overlay"
        width={WIDTH}
        height={HEIGHT}
        frag={`
#ifdef GL_ES
precision mediump float;
#endif

#extension GL_OES_standard_derivatives : enable

uniform vec2 u_resolution;
uniform vec2 u_mouse;
uniform float u_time;

#define TAU 6.2831852
#define MOD3 vec3(.1031,.11369,.13787)
#define BLACK_COL vec3(16,21,25)/255.

vec3 hash33(vec3 p3)
{
  p3 = fract(p3 * MOD3);
  p3 += dot(p3, p3.yxz+19.19);
  return -1.0 + 2.0 * fract(vec3((p3.x + p3.y)*p3.z, (p3.x+p3.z)*p3.y, (p3.y+p3.z)*p3.x));
}

float simplex_noise(vec3 p)
{
    const float K1 = 0.333333333;
    const float K2 = 0.166666667;
    
    vec3 i = floor(p + (p.x + p.y + p.z) * K1);
    vec3 d0 = p - (i - (i.x + i.y + i.z) * K2);
        
    vec3 e = step(vec3(0.0), d0 - d0.yzx);
    vec3 i1 = e * (1.0 - e.zxy);
    vec3 i2 = 1.0 - e.zxy * (1.0 - e);
    
    vec3 d1 = d0 - (i1 - 1.0 * K2);
    vec3 d2 = d0 - (i2 - 2.0 * K2);
    vec3 d3 = d0 - (1.0 - 3.0 * K2);
    
    vec4 h = max(0.6 - vec4(dot(d0, d0), dot(d1, d1), dot(d2, d2), dot(d3, d3)), 0.0);
    vec4 n = h * h * h * h * vec4(
      dot(d0, hash33(i)),
      dot(d1, hash33(i + i1)),
      dot(d2, hash33(i + i2)),
      dot(d3, hash33(i + 1.0)));
    
    return dot(vec4(31.316), n);
}

void mainImage( out vec4 fragColor, in vec2 fragCoord )
{

    float slow_time = u_time / 15.0;

    vec2 uv = 0.9 * (fragCoord.xy-u_resolution.xy*0.5) / u_resolution.y;
    float a = sin(atan(uv.y, uv.x));
    float am = abs(a-.5)/4.;
    float l = length(uv);                         
    
    float m1 = clamp(.1/smoothstep(.0, 1.75, l), 0., 1.);
    float m2 = clamp(.1/smoothstep(.42, 0., l), 0., 1.);
    float s1 = (simplex_noise(vec3(uv*2., 1. + slow_time*.525))*(max(1.0 - l*1.75, 0.)) + .9);
    float s2 = (simplex_noise(vec3(uv*1., 15. + slow_time*.525))*(max(.0 + l*1., .025)) + 1.25);
    float s3 = (
        simplex_noise(
          vec3(
              vec2(
                am,
                am * 100.0 + slow_time * 3.0
              ) * 0.15,
              30.0 + slow_time *.525
              )
          ) * (max(.0 + l*1., .25)) + 1.5
        );
    s3 *= smoothstep(0.0, .3345, l);
    
    float sh = smoothstep(0.15, .35, l);
    
    
    float m = m1*m1*m2 * ((s1*s2*s3) * (1.-l)) * sh;
    //m = clamp(m, 0., 1.);
    
    vec3 col = mix(BLACK_COL, (0.5 + 0.5*cos(slow_time+uv.xyx*3.+vec3(0,2,4))), m);
            
    fragColor = vec4(col, 1.);
}

void main(void) {
    mainImage(gl_FragColor, gl_FragCoord.xy);
}
        `}
      />
      <div className="fullscreen">
        <svg xmlns="http://www.w3.org/2000/svg" width={WIDTH} height={HEIGHT}>

          <rect
            x={`${(WIDTH / 2 - BOTTOM_BOX_WIDTH / 2)}px`}
            y="0px"
            width={`${BOTTOM_BOX_WIDTH}px`}
            height={`${BOTTOM_BOX_HEIGHT}px`}
            stroke="white"
            strokeWidth="4"
            fill="white"
          />
          <text
            x={`${WIDTH / 2}px`}
            y={`${NUDGE + (BOTTOM_BOX_HEIGHT / 2)}px`}
            textAnchor="middle"
            dominantBaseline="middle"
            fill="black"
            className="caption"
          >
            UNIX EPOCH
          </text>
          <text
            x={`${WIDTH / 2}px`}
            y={`${NUDGE + (2 * BOTTOM_BOX_HEIGHT)}px`}
            textAnchor="middle"
            dominantBaseline="middle"
            fill="rgba(0, 255, 0, 1.0)"
            className="unixtime"
          >
            {seconds.toString().replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,')}
          </text>

          <rect
            x={`${(WIDTH - BOX_WIDTH) / 2}px`}
            y={`${(HEIGHT - BOX_HEIGHT) / 2 - NUDGE}px`}
            width={`${BOX_WIDTH}px`}
            height={`${BOX_HEIGHT}px`}
            stroke="white"
            strokeWidth="4"
            fill="none"
          />
          <text
            x={`${WIDTH / 2}px`}
            y={`${NUDGE + (HEIGHT / 2)}px`}
            textAnchor="middle"
            dominantBaseline="middle"
            fill="white"
            className="bigtime"
          >
            {timeDigits(unixTime, 'America/Denver')}
          </text>
          <text
            x={`${WIDTH / 2}px`}
            y={`${(HEIGHT / 2) + (BOX_HEIGHT / 2) + 5 * NUDGE}px`}
            textAnchor="middle"
            dominantBaseline="middle"
            fill="white"
            className="caption"
          >
            MST7MDT&nbsp;
            { dateDigits(unixTime, 'America/Denver') }
          </text>
          {
            Array(60).fill(0).map((_e, minute) => {
              const angle = (2 * Math.PI * minute) / 60.0;
              const tickX = (WIDTH / 2) + RADIUS * Math.sin(angle);
              const tickY = (HEIGHT / 2) - RADIUS * Math.cos(angle);
              const tickWidth = ((minute % 5 === 0) ? 16 : 12);
              return (
                <rect
                  x={`${tickX - (tickWidth / 2)}px`}
                  y={`${tickY - (tickWidth / 2)}px`}
                  width={`${tickWidth}px`}
                  height={`${tickWidth}px`}
                  fill={minute <= minuteCount ? 'red' : 'white'}
                />
              );
            })
          }
          {
            Array(12).fill(0).map((_e, hourPosition) => {
              const angle = (2 * Math.PI * hourPosition) / 12.0;
              const numeralsX = (WIDTH / 2) + NUMERAL_RADIUS * Math.sin(angle);
              const numeralsY = (HEIGHT / 2) - NUMERAL_RADIUS * Math.cos(angle);
              return (
                <text
                  x={`${numeralsX}px`}
                  y={`${numeralsY}px`}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fill="white"
                  className="numeral"
                >
                  {
                    5 * hourPosition
                  }
                </text>
              );
            })
          }

          <text
            x={`${(WIDTH / 4)}px`}
            y={`${HEIGHT - (2 * BOTTOM_BOX_HEIGHT) - (2 * NUDGE)}px`}
            textAnchor="middle"
            dominantBaseline="middle"
            fill="red"
            className="maintime"
          >
            {timeDigits(unixTime, 'America/Los_Angeles')}
          </text>
          <rect
            x={`${((WIDTH / 4) - BOTTOM_BOX_WIDTH / 2)}px`}
            y={`${(HEIGHT - BOTTOM_BOX_HEIGHT)}px`}
            width={`${BOTTOM_BOX_WIDTH}px`}
            height={`${BOTTOM_BOX_HEIGHT}px`}
            stroke="white"
            strokeWidth="4"
            fill="white"
          />
          <text
            x={`${(WIDTH / 4)}px`}
            y={`${HEIGHT - (BOTTOM_BOX_HEIGHT / 2)}px`}
            textAnchor="middle"
            dominantBaseline="middle"
            fill="black"
            className="caption"
          >
            PST7PDT&nbsp;
            { dateDigits(unixTime, 'America/Los_Angeles') }
          </text>

          <text
            x={`${2 * (WIDTH / 4)}px`}
            y={`${HEIGHT - (2 * BOTTOM_BOX_HEIGHT) - (2 * NUDGE)}px`}
            textAnchor="middle"
            dominantBaseline="middle"
            fill="red"
            className="maintime"
          >
            {timeDigits(unixTime, 'America/New_York')}
          </text>
          <rect
            x={`${(2 * (WIDTH / 4) - BOTTOM_BOX_WIDTH / 2)}px`}
            y={`${(HEIGHT - BOTTOM_BOX_HEIGHT)}px`}
            width={`${BOTTOM_BOX_WIDTH}px`}
            height={`${BOTTOM_BOX_HEIGHT}px`}
            stroke="white"
            strokeWidth="4"
            fill="white"
          />
          <text
            x={`${2 * (WIDTH / 4)}px`}
            y={`${HEIGHT - (BOTTOM_BOX_HEIGHT / 2)}px`}
            textAnchor="middle"
            dominantBaseline="middle"
            fill="black"
            className="caption"
          >
            EST5EDT&nbsp;
            {dateDigits(unixTime, 'America/New_York')}
          </text>

          <rect
            x={`${(3 * (WIDTH / 4) - BOTTOM_BOX_WIDTH / 2)}px`}
            y={`${(HEIGHT - BOTTOM_BOX_HEIGHT)}px`}
            width={`${BOTTOM_BOX_WIDTH}px`}
            height={`${BOTTOM_BOX_HEIGHT}px`}
            stroke="white"
            strokeWidth="4"
            fill="white"
          />
          <text
            x={`${3 * (WIDTH / 4)}px`}
            y={`${HEIGHT - (BOTTOM_BOX_HEIGHT / 2)}px`}
            textAnchor="middle"
            dominantBaseline="middle"
            fill="black"
            className="caption"
          >
            UTC&nbsp;
            {dateDigits(unixTime, 'UTC')}
          </text>
          <text
            x={`${3 * (WIDTH / 4)}px`}
            y={`${HEIGHT - (2 * BOTTOM_BOX_HEIGHT) - (2 * NUDGE)}px`}
            textAnchor="middle"
            dominantBaseline="middle"
            fill="red"
            className="maintime"
          >
            {timeDigits(unixTime, 'UTC')}
          </text>
        </svg>
      </div>
    </div>
  );

};

Sundial.propTypes = { refreshInterval: PropTypes.number };

Sundial.defaultProps = { refreshInterval: 500 };

export default Sundial;
